import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import Select from "../../components/ReportSelect";
import { Container, Table, Button, Row, Col, Spinner } from "react-bootstrap";
import Pagination from "../../components/Pagination";
import { saveAs } from "file-saver"; // ⭐ For CSV export
import "../../styles/Reports.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const fmt = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "");

export default function TerminationReport() {
  const navigate = useNavigate();

  const today = dayjs().format("YYYY-MM-DD");
  const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD");

  // Dropdowns
  const [orgUnits, setOrgUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [groups, setGroups] = useState([]);

  // Filters
  const [pStartDate, setPStartDate] = useState(oneMonthAgo);
  const [pEndDate, setPEndDate] = useState(today);
  const [pOrgUnit, setPOrgUnit] = useState(null);
  const [pCategory, setPCategory] = useState(null);
  const [pSubCat, setPSubCat] = useState(null);
  const [pGroup, setPGroup] = useState(null);

  const [filters, setFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  // Load dropdowns
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    async function load() {
      try {
        const [ou, cat, sc, grp] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/position-groups`, { headers }),
        ]);

        setOrgUnits(ou.data.map((o) => ({ value: o.ou_id, label: o.ou_name })));
        setCategories(
          cat.data.map((c) => ({ value: c.c_id, label: c.c_name }))
        );
        setSubCats(sc.data.map((s) => ({ value: s.sc_id, label: s.sc_name })));
        setGroups(grp.data.map((g) => ({ value: g.pg_id, label: g.pg_name })));
      } catch (err) {
        console.error("Dropdown load error:", err);
      }
    }

    load();
  }, []);

  // Fetch data
  const fetchRows = useCallback(async () => {
    if (!hasSearched) return;

    setLoading(true);
    setCurrentPage(1);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(
        `${BASE_URL}/api/reports/termination-report`,
        {
          headers,
          params: filters,
        }
      );

      setRows(res.data || []);
    } catch (err) {
      console.error("Termination report fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, hasSearched]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Apply filters
  const applyFilters = () => {
    setHasSearched(true);
    setFilters({
      startDate: pStartDate,
      endDate: pEndDate,
      orgUnit: pOrgUnit?.value || "",
      category: pCategory?.value || "",
      subCategory: pSubCat?.value || "",
      group: pGroup?.value || "",
    });
  };

  // Clear filters
  const clearFilters = () => {
    setPStartDate(oneMonthAgo);
    setPEndDate(today);
    setPOrgUnit(null);
    setPCategory(null);
    setPSubCat(null);
    setPGroup(null);

    setFilters({});
    setRows([]);
    setHasSearched(false);
  };

  // Pagination
  const paginated = rows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ⭐ EXPORT CSV
  const exportCSV = () => {
    if (!hasSearched || rows.length === 0) {
      alert("No data to export.");
      return;
    }

    const headers = [
      "Org Unit",
      "Recruiter",
      "Driver Manager",
      "First Name",
      "Last Name",
      "Employment Sub-Category",
      "Position",
      "Termination Date",
    ];

    const lines = rows.map((r) =>
      [
        r.org_unit,
        r.recruiter,
        r.driver_manager,
        r.first_name,
        r.last_name,
        r.emp_sub_category,
        r.position,
        fmt(r.termination_date),
      ]
        .map((c) => `"${String(c || "").replace(/"/g, '""')}"`)
        .join(",")
    );

    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    saveAs(blob, "TerminationReport.csv");
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="fw-bold m-0">🛑 Termination Report</h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Filters */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            value={pStartDate}
            onChange={(e) => setPStartDate(e.target.value)}
          />
        </Col>

        <Col>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            value={pEndDate}
            onChange={(e) => setPEndDate(e.target.value)}
          />
        </Col>

        <Col>
          <label>Org Unit</label>
          <Select value={pOrgUnit} onChange={setPOrgUnit} options={orgUnits} />
        </Col>

        <Col>
          <label>Groups</label>
          <Select value={pGroup} onChange={setPGroup} options={groups} />
        </Col>
      </Row>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md="3">
          <label>Employment Category</label>
          <Select
            value={pCategory}
            onChange={setPCategory}
            options={categories}
          />
        </Col>

        <Col md="3">
          <label>Employment Sub-category</label>
          <Select value={pSubCat} onChange={setPSubCat} options={subCats} />
        </Col>
      </Row>

      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto" className="d-flex align-items-end">
          <Button className="me-2" onClick={applyFilters}>
            🔍 Filter
          </Button>
          <Button variant="secondary" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Col>

        {/* ⭐ EXPORT BUTTON HERE */}
        <Col md="auto" className="d-flex align-items-end ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || rows.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use filters above and click <strong>Filter</strong> to load results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm">
            <thead className="table-dark text-center">
              <tr>
                <th>Org Unit</th>
                <th>Recruiter</th>
                <th>Driver Manager</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Employment Sub-Category</th>
                <th>Position</th>
                <th>Termination Date</th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted">
                    No results found.
                  </td>
                </tr>
              ) : (
                paginated.map((r, i) => (
                  <tr key={i}>
                    <td>{r.org_unit}</td>
                    <td>{r.recruiter}</td>
                    <td>{r.driver_manager}</td>
                    <td>{r.first_name}</td>
                    <td>{r.last_name}</td>
                    <td>{r.emp_sub_category}</td>
                    <td>{r.position}</td>
                    <td>{fmt(r.termination_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={rows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
